import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

/**
 * ThemeProvider for Spark Dating App
 * 
 * Manages app-wide theming with CSS variable injection.
 * Themes are stored in localStorage so they persist across sessions.
 * 
 * Usage:
 *   Wrap your app: <ThemeProvider><DatingApp /></ThemeProvider>
 *   In any component: const { theme, setTheme, themes } = useTheme();
 */

// ─── Theme Definitions ──────────────────────────────────────────────
const THEMES = {
  default: {
    id: 'default',
    name: 'Spark',
    emoji: '💜',
    description: 'Classic indigo vibes',
    preview: ['#6366f1', '#8b5cf6', '#ffffff'],
    vars: {
      '--primary': '#6366f1',
      '--primary-dark': '#4f46e5',
      '--primary-light': '#a5b4fc',
      '--accent': '#f43f5e',
      '--accent-light': '#fda4af',
      '--surface': '#ffffff',
      '--surface-dim': '#f8fafc',
      '--surface-alt': '#f1f5f9',
      '--border': '#e2e8f0',
      '--border-light': '#f1f5f9',
      '--text-primary': '#0f172a',
      '--text-secondary': '#64748b',
      '--text-muted': '#94a3b8',
      '--success': '#22c55e',
      '--warning': '#f59e0b',
      '--danger': '#ef4444',
      '--header-bg': 'linear-gradient(160deg, #6366f1 0%, #7c3aed 100%)',
      '--page-bg': 'linear-gradient(160deg, #6366f1 0%, #8b5cf6 50%, #a855f7 100%)',
      '--card-bg': '#ffffff',
      '--card-shadow': '0 20px 60px rgba(0,0,0,0.15)',
      '--msg-mine': '#6366f1',
      '--msg-theirs': '#f1f5f9',
      '--msg-mine-text': '#ffffff',
      '--msg-theirs-text': '#0f172a',
      '--nav-active-bg': '#ffffff',
      '--nav-active-text': '#6366f1',
      '--nav-inactive-text': 'rgba(255,255,255,0.7)',
      '--nav-bar-bg': 'rgba(0,0,0,0.15)',
    }
  },

  midnight: {
    id: 'midnight',
    name: 'Midnight',
    emoji: '🌙',
    description: 'Easy on the eyes',
    preview: ['#1e1b4b', '#312e81', '#1e1e2e'],
    vars: {
      '--primary': '#818cf8',
      '--primary-dark': '#6366f1',
      '--primary-light': '#4338ca',
      '--accent': '#fb7185',
      '--accent-light': '#9f1239',
      '--surface': '#1e1e2e',
      '--surface-dim': '#181825',
      '--surface-alt': '#262637',
      '--border': '#313244',
      '--border-light': '#2a2a3c',
      '--text-primary': '#e2e8f0',
      '--text-secondary': '#94a3b8',
      '--text-muted': '#64748b',
      '--success': '#34d399',
      '--warning': '#fbbf24',
      '--danger': '#f87171',
      '--header-bg': 'linear-gradient(160deg, #1e1b4b 0%, #312e81 100%)',
      '--page-bg': 'linear-gradient(160deg, #0f0e1a 0%, #1a1830 50%, #1e1b4b 100%)',
      '--card-bg': '#1e1e2e',
      '--card-shadow': '0 20px 60px rgba(0,0,0,0.5)',
      '--msg-mine': '#6366f1',
      '--msg-theirs': '#262637',
      '--msg-mine-text': '#ffffff',
      '--msg-theirs-text': '#e2e8f0',
      '--nav-active-bg': '#262637',
      '--nav-active-text': '#818cf8',
      '--nav-inactive-text': 'rgba(255,255,255,0.45)',
      '--nav-bar-bg': 'rgba(0,0,0,0.35)',
    }
  },

  rose: {
    id: 'rose',
    name: 'Rosé',
    emoji: '🌹',
    description: 'Warm & romantic',
    preview: ['#e11d48', '#f43f5e', '#fff1f2'],
    vars: {
      '--primary': '#e11d48',
      '--primary-dark': '#be123c',
      '--primary-light': '#fda4af',
      '--accent': '#7c3aed',
      '--accent-light': '#c4b5fd',
      '--surface': '#ffffff',
      '--surface-dim': '#fff1f2',
      '--surface-alt': '#ffe4e6',
      '--border': '#fecdd3',
      '--border-light': '#ffe4e6',
      '--text-primary': '#1c1917',
      '--text-secondary': '#78716c',
      '--text-muted': '#a8a29e',
      '--success': '#22c55e',
      '--warning': '#f59e0b',
      '--danger': '#ef4444',
      '--header-bg': 'linear-gradient(160deg, #e11d48 0%, #f43f5e 100%)',
      '--page-bg': 'linear-gradient(160deg, #be123c 0%, #e11d48 50%, #f43f5e 100%)',
      '--card-bg': '#ffffff',
      '--card-shadow': '0 20px 60px rgba(225,29,72,0.15)',
      '--msg-mine': '#e11d48',
      '--msg-theirs': '#ffe4e6',
      '--msg-mine-text': '#ffffff',
      '--msg-theirs-text': '#1c1917',
      '--nav-active-bg': '#ffffff',
      '--nav-active-text': '#e11d48',
      '--nav-inactive-text': 'rgba(255,255,255,0.7)',
      '--nav-bar-bg': 'rgba(0,0,0,0.15)',
    }
  },

  sunset: {
    id: 'sunset',
    name: 'Sunset',
    emoji: '🌅',
    description: 'Golden hour energy',
    preview: ['#ea580c', '#f97316', '#fffbeb'],
    vars: {
      '--primary': '#ea580c',
      '--primary-dark': '#c2410c',
      '--primary-light': '#fdba74',
      '--accent': '#0891b2',
      '--accent-light': '#67e8f9',
      '--surface': '#ffffff',
      '--surface-dim': '#fffbeb',
      '--surface-alt': '#fef3c7',
      '--border': '#fed7aa',
      '--border-light': '#ffedd5',
      '--text-primary': '#1c1917',
      '--text-secondary': '#78716c',
      '--text-muted': '#a8a29e',
      '--success': '#22c55e',
      '--warning': '#f59e0b',
      '--danger': '#ef4444',
      '--header-bg': 'linear-gradient(160deg, #ea580c 0%, #f97316 50%, #fb923c 100%)',
      '--page-bg': 'linear-gradient(160deg, #c2410c 0%, #ea580c 50%, #f97316 100%)',
      '--card-bg': '#ffffff',
      '--card-shadow': '0 20px 60px rgba(234,88,12,0.12)',
      '--msg-mine': '#ea580c',
      '--msg-theirs': '#ffedd5',
      '--msg-mine-text': '#ffffff',
      '--msg-theirs-text': '#1c1917',
      '--nav-active-bg': '#ffffff',
      '--nav-active-text': '#ea580c',
      '--nav-inactive-text': 'rgba(255,255,255,0.7)',
      '--nav-bar-bg': 'rgba(0,0,0,0.15)',
    }
  },

  ocean: {
    id: 'ocean',
    name: 'Ocean',
    emoji: '🌊',
    description: 'Cool & refreshing',
    preview: ['#0891b2', '#06b6d4', '#ecfeff'],
    vars: {
      '--primary': '#0891b2',
      '--primary-dark': '#0e7490',
      '--primary-light': '#67e8f9',
      '--accent': '#e11d48',
      '--accent-light': '#fda4af',
      '--surface': '#ffffff',
      '--surface-dim': '#ecfeff',
      '--surface-alt': '#cffafe',
      '--border': '#a5f3fc',
      '--border-light': '#cffafe',
      '--text-primary': '#0f172a',
      '--text-secondary': '#64748b',
      '--text-muted': '#94a3b8',
      '--success': '#22c55e',
      '--warning': '#f59e0b',
      '--danger': '#ef4444',
      '--header-bg': 'linear-gradient(160deg, #0e7490 0%, #0891b2 50%, #06b6d4 100%)',
      '--page-bg': 'linear-gradient(160deg, #0e7490 0%, #0891b2 50%, #22d3ee 100%)',
      '--card-bg': '#ffffff',
      '--card-shadow': '0 20px 60px rgba(8,145,178,0.12)',
      '--msg-mine': '#0891b2',
      '--msg-theirs': '#cffafe',
      '--msg-mine-text': '#ffffff',
      '--msg-theirs-text': '#0f172a',
      '--nav-active-bg': '#ffffff',
      '--nav-active-text': '#0891b2',
      '--nav-inactive-text': 'rgba(255,255,255,0.7)',
      '--nav-bar-bg': 'rgba(0,0,0,0.15)',
    }
  },

  amoled: {
    id: 'amoled',
    name: 'AMOLED Dark',
    emoji: '🖤',
    description: 'True black, saves battery',
    preview: ['#000000', '#18181b', '#000000'],
    vars: {
      '--primary': '#a78bfa',
      '--primary-dark': '#8b5cf6',
      '--primary-light': '#6d28d9',
      '--accent': '#fb7185',
      '--accent-light': '#9f1239',
      '--surface': '#09090b',
      '--surface-dim': '#000000',
      '--surface-alt': '#18181b',
      '--border': '#27272a',
      '--border-light': '#1c1c1f',
      '--text-primary': '#fafafa',
      '--text-secondary': '#a1a1aa',
      '--text-muted': '#71717a',
      '--success': '#4ade80',
      '--warning': '#fbbf24',
      '--danger': '#f87171',
      '--header-bg': 'linear-gradient(160deg, #18181b 0%, #09090b 100%)',
      '--page-bg': '#000000',
      '--card-bg': '#09090b',
      '--card-shadow': '0 20px 60px rgba(0,0,0,0.8)',
      '--msg-mine': '#7c3aed',
      '--msg-theirs': '#18181b',
      '--msg-mine-text': '#ffffff',
      '--msg-theirs-text': '#fafafa',
      '--nav-active-bg': '#18181b',
      '--nav-active-text': '#a78bfa',
      '--nav-inactive-text': 'rgba(255,255,255,0.35)',
      '--nav-bar-bg': 'rgba(255,255,255,0.06)',
    }
  },
};

// ─── Context ──────────────────────────────────────────────────────────
const ThemeContext = createContext(null);

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    // Fallback for components outside provider
    return { theme: THEMES.default, themeId: 'default', setTheme: () => {}, themes: THEMES };
  }
  return ctx;
}

export function ThemeProvider({ children }) {
  const [themeId, setThemeId] = useState(() => {
    try { return localStorage.getItem('spark-theme') || 'default'; }
    catch { return 'default'; }
  });

  const theme = THEMES[themeId] || THEMES.default;

  // Apply CSS variables to :root whenever theme changes
  useEffect(() => {
    const root = document.documentElement;
    Object.entries(theme.vars).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });
    try { localStorage.setItem('spark-theme', themeId); } catch {}
  }, [themeId, theme]);

  const setTheme = useCallback((id) => {
    if (THEMES[id]) setThemeId(id);
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, themeId, setTheme, themes: THEMES }}>
      {children}
    </ThemeContext.Provider>
  );
}

export { THEMES };
export default ThemeProvider;
